import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { isSuperAdminRole } from "../Graphql/types/auth";

export type AdminAuthStatus =
  | "loading"
  | "authenticated"
  | "access_denied"
  | "unauthenticated";

export interface AdminUser {
  userId: string;
  role: string;
  companyId?: string | null;
  storeId?: string | null;
}

interface AdminAuthState {
  status: AdminAuthStatus;
  user: AdminUser | null;
}

interface AdminAuthContextValue extends AdminAuthState {
  setAuthenticated: (user: AdminUser) => void;
  setAccessDenied: () => void;
  setUnauthenticated: () => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

const defaultState: AdminAuthState = {
  status: "unauthenticated",
  user: null,
};

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return ctx;
}

interface AdminAuthProviderProps {
  children: ReactNode;
}

export function AdminAuthProvider({ children }: AdminAuthProviderProps) {
  const [state, setState] = useState<AdminAuthState>(defaultState);

  const setAuthenticated = useCallback((user: AdminUser) => {
    setState({ status: "authenticated", user });
  }, []);

  const setAccessDenied = useCallback(() => {
    setState({ status: "access_denied", user: null });
  }, []);

  const setUnauthenticated = useCallback(() => {
    setState({ status: "unauthenticated", user: null });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setState((prev) => ({
      ...prev,
      status: loading ? "loading" : prev.status,
    }));
  }, []);

  const logout = useCallback(() => {
    setState(defaultState);
  }, []);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      ...state,
      setAuthenticated,
      setAccessDenied,
      setUnauthenticated,
      setLoading,
      logout,
    }),
    [
      state.status,
      state.user,
      setAuthenticated,
      setAccessDenied,
      setUnauthenticated,
      setLoading,
      logout,
    ],
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export { isSuperAdminRole };
